/**
 * The runs behind a work timeline, in the console: the workbench's `hooks/useWorkTimeline.ts` with
 * its desktop transport (`shell.invoke('workTimeline.get')`, `runs-get` over the host link) swapped
 * for the project's own runs route, `GET /projects/v1/:id/runs?from&to&limit` (routes/projectRuns.ts).
 * The answer is the same record (`WorkRun`, shared/schemas/projectRuns.ts), so the model takes it as
 * the desktop's: `asTimelineRuns` fills in the repository's name, how long and whether it is still
 * going; the person who asked is named from the answer's `people`.
 *
 * Kept fresh the way every console view is (`useLoad`): read when it opens and whenever the range
 * changes, again every `pollMs`. A new range keeps showing what was there until its answer comes, so
 * zooming out does not flash empty. The console's runs are every repository's and every member's:
 * there is no repository scope here, only the project's.
 */
import { runMatches, runTotals, type RunFilter, type RunTotals } from '@aioe/shared';
import type { ProjectHomeKind } from '@aioe/shared/protocol/hostKinds';
import { useMemo } from 'react';

import { routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { asTimelineRuns, type TimelineRun } from './workTimelineModel.ts';

export type TimelineScope = 'project';

export interface WorkTimelineAsk {
  scope: TimelineScope;
  /** The project's id (`prj_…`). */
  projectId?: string;
  from: number;
  to: number;
  filter?: RunFilter;
  enabled?: boolean;
  pollMs?: number;
}

export interface WorkTimelineData {
  runs: TimelineRun[];
  totals: RunTotals | undefined;
  /** When it was read: "now" for a run still going. */
  at: number;
  error?: string;
  truncated?: boolean;
  /** Waiting for the first answer for what is asked now. */
  loading: boolean;
  /** The project's repositories seen in the answer, by id. */
  repositories: ReadonlyArray<{ rootId: string; name: string }>;
  /** Where the project's runs are kept: always the organisation's, here. */
  home?: ProjectHomeKind;
  refresh(): void;
}

/** The most runs one answer holds (the route's own cap). */
const LIMIT = 2000;
const EMPTY: TimelineRun[] = [];

export function useWorkTimeline(ask: WorkTimelineAsk): WorkTimelineData {
  const { projectId, from, to, filter, pollMs = 30_000 } = ask;
  const enabled = ask.enabled !== false && !!projectId;
  const load = useLoad(
    async () => {
      if (!enabled || !projectId) return undefined;
      const answer = await routes.projectRuns(projectId, { from: new Date(from).toISOString(), to: new Date(to).toISOString(), limit: LIMIT });
      return { answer, at: Date.now() };
    },
    [enabled, projectId, from, to],
    enabled && pollMs > 0 ? pollMs : undefined,
  );
  const filterKey = JSON.stringify(filter ?? null);
  const data = load.data;
  return useMemo(() => {
    const f = JSON.parse(filterKey) as RunFilter | null;
    const at = data?.at ?? 0;
    const people = data?.answer.people ?? {};
    const names = new Map<string, string>();
    for (const r of data?.answer.runs ?? []) if (r.rootName && !names.has(r.rootId)) names.set(r.rootId, r.rootName);
    const kept = (data?.answer.runs ?? []).filter((r) => !f || runMatches(r, f));
    const runs = data
      ? asTimelineRuns(kept, at, (rootId) => names.get(rootId) ?? (rootId || 'the project')).map((r) => {
          const who = r.trigger === 'person' && r.by?.principal ? people[r.by.principal]?.name : undefined;
          return who ? { ...r, byName: who } : r;
        })
      : EMPTY;
    return {
      runs,
      totals: data ? runTotals(kept, at) : undefined,
      at,
      ...(load.error ? { error: load.error } : {}),
      ...(data?.answer.truncated ? { truncated: true } : {}),
      loading: enabled && load.loading,
      repositories: [...names].map(([rootId, name]) => ({ rootId, name })),
      home: 'organisation' as const,
      refresh: load.reload,
    };
  }, [data, filterKey, load.error, load.loading, load.reload, enabled]);
}
